import { HttpErrorResponse, HttpEventType, HttpHandlerFn, HttpRequest } from "@angular/common/http";
import { catchError, switchMap, tap, throwError } from "rxjs";
import { AuthService } from "../services/auth.service";
import { inject } from "@angular/core";
import { environment } from "@/environments/environment";
import { ToastService } from "../services/toast.service";
import { Router } from "@angular/router";
import { ErrorDataService } from "../services/error-data.service";
import { ErrorLevel } from "../models/error-level";

export function unauthorizedInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn) {
  // Only intercept calls to our own backend API
  if (!req.url.includes(environment.apiUrl)) return next(req);

  const authService = inject(AuthService);
  const toastService = inject(ToastService);
  const router = inject(Router);
  const errorDataService = inject(ErrorDataService);

  const handleErrorResponse = (error: HttpErrorResponse, redirectToErrorPage: boolean) => {
    if (!error?.error?.['message'] || !error?.error?.['description']) {
      toastService.show('error', 'Unable to complete your request because of an unexpected error.', 'Error: Something Went Wrong');
    }
    else if (redirectToErrorPage) {
      errorDataService.setError(error);
      router.navigate(['/main/error']);
    }
    else {
      toastService.show(
        'error',
        `${error.error['message']}`,
        `${error.status}: ${error.error['description']}`
      );
    }
  };

  return next(req).pipe(
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse) {
        const redirectToErrorPage = error.error?.['scope'] === ErrorLevel.PAGE;

        if (error.status === 403) {
          return authService.triggerTokenRefresh().pipe(
            switchMap(accessToken => {
              const newReq = req.clone({
                setHeaders: {
                  Authorization: `Bearer ${accessToken}`,
                },
              });

              // Retry the request
              return next(newReq).pipe(
                tap(event => {
                  if (event.type === HttpEventType.Response) {
                    // Optionally handle response
                  }
                }),
                catchError(retryError => {
                  const retryErrorRedirectToErrorPage = retryError.error?.['scope'] === ErrorLevel.PAGE;
                  handleErrorResponse(retryError, retryErrorRedirectToErrorPage);
                  return throwError(() => retryError);
                })
              );
            }),
            catchError(refreshError => {
              // Refresh token failed or session expired
              console.error('Token refresh failed', refreshError);
              authService.logout();
              return throwError(() => refreshError);
            })
          );
        }
        else {
          handleErrorResponse(error, redirectToErrorPage);
        }
      }

      // Return the error to caller
      return throwError(() => error);
    })
  );
}
